(function (root, factory) {
  const createPageTrackingElementClick = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = createPageTrackingElementClick;
    return;
  }

  if (root) {
    root.createPageTrackingElementClick = createPageTrackingElementClick;
  }
})(typeof window !== 'undefined' ? window : undefined, function createPageTrackingElementClickFactory() {
  return function createPageTrackingElementClick(core, options) {
    const currentOptions = options || {};
    const pageIdentity = currentOptions.pageIdentity || {
      pageName: '',
      subPageName: '',
      pageId: ''
    };

    function isElementNode(node) {
      return Boolean(node && node.nodeType === 1);
    }

    function hasExplicitTrackingMetadata(element) {
      if (!element || !element.dataset) {
        return false;
      }

      return Boolean(
        element.dataset.trackType ||
        element.dataset.trackName ||
        element.dataset.trackId ||
        element.dataset.trackPosition
      );
    }

    function isTrackableClickElement(element) {
      if (!element || !element.isConnected) {
        return false;
      }

      const computedStyle = typeof core.window.getComputedStyle === 'function'
        ? core.window.getComputedStyle(element)
        : null;

      return core.utils.isTrackableElementCandidate({
        tagName: element.tagName,
        href: element.getAttribute('href'),
        explicitType: element.dataset && element.dataset.trackType,
        hidden: element.hidden,
        ariaHidden: element.getAttribute('aria-hidden'),
        disabled: element.disabled,
        display: computedStyle && computedStyle.display,
        visibility: computedStyle && computedStyle.visibility
      });
    }

    function resolveTrackableClickElement(eventTarget) {
      let currentNode = isElementNode(eventTarget) ? eventTarget : eventTarget && eventTarget.parentElement;
      let nativeCandidate = null;

      while (currentNode && currentNode !== core.document.body && currentNode !== core.document.documentElement) {
        if (isTrackableClickElement(currentNode)) {
          if (hasExplicitTrackingMetadata(currentNode)) {
            return currentNode;
          }

          if (!nativeCandidate) {
            nativeCandidate = currentNode;
          }
        }

        currentNode = currentNode.parentElement;
      }

      if (currentNode && isTrackableClickElement(currentNode) && hasExplicitTrackingMetadata(currentNode)) {
        return currentNode;
      }

      return nativeCandidate;
    }

    function resolveClickedImageAltText(eventTarget, trackableElement) {
      let currentNode = isElementNode(eventTarget) ? eventTarget : eventTarget && eventTarget.parentElement;

      while (currentNode && currentNode !== trackableElement && currentNode !== core.document.body) {
        if (String(currentNode.tagName || '').toLowerCase() === 'img') {
          return currentNode.getAttribute('alt') || '';
        }

        currentNode = currentNode.parentElement;
      }

      if (trackableElement && String(trackableElement.tagName || '').toLowerCase() === 'img') {
        return trackableElement.getAttribute('alt') || '';
      }

      return '';
    }

    function resolveNearestCommerceProductFields(element) {
      let currentNode = isElementNode(element) ? element : null;

      while (currentNode && currentNode !== core.document.body && currentNode !== core.document.documentElement) {
        if (currentNode.dataset) {
          const productId = core.utils.fillEmptyValue(currentNode.dataset.commerceProductId);
          const productName = core.utils.fillEmptyValue(currentNode.dataset.commerceProductName);

          if (productId || productName) {
            return {
              productId: productId,
              productName: productName
            };
          }
        }

        currentNode = currentNode.parentElement;
      }

      return {
        productId: '',
        productName: ''
      };
    }

    function buildSharedElementTrackingFields(element, clickedImageAltText) {
      const metadata = core.utils.collectElementAncestorMetadata(element, core.document.body);
      const commerceProductFields = resolveNearestCommerceProductFields(element);
      const sharedFields = core.utils.buildElementTrackingData({
        pageIdentity: pageIdentity,
        trackingContext: core.trackingContext,
        elementInfo: {
          explicitName: element.dataset && element.dataset.trackName,
          explicitId: (element.dataset && element.dataset.trackId) || element.id,
          explicitType: element.dataset && element.dataset.trackType,
          explicitPosition: element.dataset && element.dataset.trackPosition,
          ariaLabel: element.getAttribute('aria-label'),
          title: element.getAttribute('title'),
          altText: clickedImageAltText || element.getAttribute('alt'),
          textContent: element.textContent,
          hrefToken: core.utils.getElementHrefToken(
            element,
            core.window.location && core.window.location.origin ? core.window.location.origin : ''
          ),
          tagName: element.tagName,
          role: element.getAttribute('role'),
          ancestorTags: metadata.ancestorTags,
          ancestorClassNames: metadata.ancestorClassNames,
          srcUrl: element.getAttribute('src')
        }
      });

      return Object.assign({}, sharedFields, {
        productId: commerceProductFields.productId || sharedFields.productId,
        productName: commerceProductFields.productName || sharedFields.productName
      });
    }

    function buildElementClickData(element, now, clickedImageAltText) {
      const sharedFields = buildSharedElementTrackingFields(element, clickedImageAltText);
      const popupName = core.utils.findPopupNameForClickContext
        ? core.utils.findPopupNameForClickContext(element, core.document.body)
        : '';
      
      const pageTrackingContext = core.window && core.window.pageTrackingContext;

      return core.buildDataInfo({
        type: core.config.eventTypes && core.config.eventTypes.elementClick,
        pageName: sharedFields.pageName,
        subPageName: sharedFields.subPageName,
        elementName: sharedFields.elementName,
        elementType: sharedFields.elementType,
        productId: pageTrackingContext.productId,
        productName: pageTrackingContext.productTitle || sharedFields.productName,
        position: sharedFields.position,
        popupName: core.utils.fillEmptyValue(popupName)
      }, now);
    }

    function reportElementClick(element, sessionModule, sessionCallbacks, clickedImageAltText) {
      const nowMs = Date.now();
      const dataInfo = buildElementClickData(element, new Date(nowMs), clickedImageAltText);

      if (!dataInfo.type || !dataInfo.elementName || !dataInfo.elementType) {
        return false;
      }

      if (sessionModule && typeof sessionModule.ensureActiveSession === 'function') {
        sessionModule.ensureActiveSession(nowMs, sessionCallbacks || {});
      }

      core.markActivity(nowMs);

      return core.sendTrackingEvent({
        basicInfo: core.buildBasicInfo(),
        dataInfo: dataInfo
      }, true);
    }

    function bindClickEvents(sessionModule, sessionCallbacks) {
      if (!core.canTrack() || !core.config.eventTypes || !core.config.eventTypes.elementClick) {
        return false;
      }

      // Use capture phase so controls like cart remove links are tracked
      // before their own click handlers mutate DOM or consume the action.
      core.document.addEventListener('click', function handleTrackedElementClick(event) {
        const trackableElement = resolveTrackableClickElement(event.target);
        const clickedImageAltText = resolveClickedImageAltText(event.target, trackableElement);

        if (!trackableElement) {
          return;
        }

        reportElementClick(trackableElement, sessionModule, sessionCallbacks, clickedImageAltText);
      }, true);

      return true;
    }

    return {
      hasExplicitTrackingMetadata,
      isTrackableClickElement,
      resolveTrackableClickElement,
      resolveClickedImageAltText,
      resolveNearestCommerceProductFields,
      buildSharedElementTrackingFields,
      buildElementClickData,
      reportElementClick,
      bindClickEvents
    };
  };
});
